<template>
  <div class="no-padding">

    <ErrorPopup
      :error="error_popup_message"
    />

    <MemberPasswordUpdate
      v-if="local_member && local_member.member_id"
      ref="passwordModal"
      :isProfilePage="false"
      :member_id="local_member.member_id"
    />

    <SPARLookup
      ref="spar-lookup-popup"
      :personnr="local_member.in_personnr"
      :member_id="local_member.member_id"
      @save="save_from_spar"
    />

    <h5>{{ $t('MEMBER_EDITOR.PERSONAL_DETAILS') }}</h5>

    <div>

       <m-row class="mt-2">
        <m-col lg="6"></m-col>
        <m-col lg="6" style="padding-left: 0px;">
          <memlist-checkbox
            :numeric="true"
            v-model="local_member.hd"
            :title="$t('MEMBER.HD')"
          />
        </m-col>
      </m-row>

      <m-row class="mt-4" v-if="unlocked_field('in_personnr')">
        <m-col lg="12" class="position-relative personnr-row">

          <memlist-text-input
            v-model="local_member.in_personnr"
            :title="$t('MEMBER.PERSONNR')"
            layout="horizontal"
            :type="secret_type"
            size="sm"
            @input="change_personnr"
            :state="validate_field(v$.local_member.in_personnr)"
            :required="constreq.includes('MEMBER_REQ_SSN')"
          />

          <span
            v-if="v$.local_member.in_personnr.$model"
            class="svg-icon svg-icon-sm personnr-status-icon"
            :class="valid_personnr ? 'text-success' : 'text-danger'"
          >
            <inline-svg
              :src="valid_personnr
                ? '/assets/svg/check-circle.svg'
                : '/assets/svg/x-circle.svg'"
            />
          </span>

        </m-col>
      </m-row>

      <m-row class="mt-4" v-if="duplicate_personnr.length > 0">
        <m-col lg="6">
          <p style="text-align: right; font-weight: 500;">{{ $t('MEMBER.DUPLICATES_PERSONNR') }}</p>
        </m-col>
        <m-col lg="6" style="padding-left: 0px;">
          <!-- add a list of members with same email that can be clicked: Email (public_id) -->
          <ul class="list-unstyled mb-0">
            <li
              v-for="m in duplicate_personnr"
              :key="m.member_id"
            >
              <a
                href="#"
                @click.prevent="select_member(m.member_id)"
              >
                {{ m.in_personnr }} <span v-if="m.public_id">({{ m.public_id }})</span>
              </a>
            </li>
          </ul>
        </m-col>
      </m-row>

      <m-row class="mt-2">
        <m-col lg="6"></m-col>
        <m-col lg="6" style="padding-left: 0px;">
          <memlist-button :disabled="!valid_personnr" variant="outline-success" size="sm" @click="open_spar_popup">{{$t('MEMBER.DO_SPAR_LOOKUP')}}</memlist-button>

          <p v-if="show_error_spar" class="error-spar">{{ $t('MEMBER.MUST_CREATE_BEFORE_SPAR') }}</p>
        </m-col>
      </m-row>

      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('firstname')"
        v-model="local_member.firstname"
        :title="$t('MEMBER.FIRSTNAME')"
        layout="horizontal"
        :type="secret_type"
        size="sm"
        @input="change_data"
        :state="validate_field(v$.local_member.firstname)"
        :required="constreq.includes('MEMBER_REQ_FULLNAME')"
      />

      <memlist-text-input
        v-if="unlocked_field('lastname')"
        class="mt-4"
        v-model="local_member.lastname"
        :title="$t('MEMBER.LASTNAME')"
        layout="horizontal"
        :type="secret_type"
        size="sm"
        @input="change_data"
        :state="validate_field(v$.local_member.lastname)"
        :required="constreq.includes('MEMBER_REQ_FULLNAME')"
      />
      
      <memlist-text-input
        v-if="unlocked_field('dob')"
        class="mt-4"
        v-model="local_member.dob"
        :title="$t('MEMBER.DOB')"
        layout="horizontal"
        :type="secret_type"
        size="sm"
        @input="change_data"
        :state="validate_field(v$.local_member.dob)"
        :required="constreq.includes('MEMBER_REQ_PERSONNR')"
      />
      
      <m-row class="mt-4" v-if="unlocked_field('email')">

        <m-col lg="12">

          <memlist-text-input
            v-model="local_member.email"
            :title="$t('MEMBER.EMAIL')"
            layout="horizontal"
            :type="secret_type"
            size="sm"
            @input="change_email"
            :required="constreq.includes('MEMBER_REQ_EMAIL')"
            :state="validate_field(v$.local_member.email)"
          />
        </m-col>
      </m-row>

      <m-row v-if="unlocked_field('email')">
        <m-col lg="12">

          <memlist-text-input
            v-model="translated_email_status"
            :title="$t('MEMBER.EMAIL_STATUS')"
            layout="horizontal"
            size="sm"
            :disabled="true"
          />
        </m-col>
      </m-row>


      <m-row class="mt-4" v-if="duplicate_emails.length > 0">
        <m-col lg="6">
          <p style="text-align: right; font-weight: 500;">{{ $t('MEMBER.DUPLICATES_EMAIL') }}</p>
        </m-col>
        <m-col lg="6" style="padding-left: 0px;">
          <!-- add a list of members with same email that can be clicked: Email (public_id) -->
          <ul class="list-unstyled mb-0">
            <li
              v-for="m in duplicate_emails"
              :key="m.member_id"
            >
              <a
                href="#"
                @click.prevent="select_member(m.member_id)"
              >
                {{ m.email }} <span v-if="m.public_id">({{ m.public_id }})</span>
              </a>
            </li>
          </ul>
        </m-col>
      </m-row>

      <m-row class="mt-4" v-if="unlocked_field('email2')">
        <m-col lg="12">
          <memlist-text-input
            v-model="local_member.email2"
            :title="$t('MEMBER.EMAIL2')"
            layout="horizontal"
            :type="secret_type"
            size="sm"
            @input="change_email2"
            :state="validate_field(v$.local_member.email2)"
          />
        </m-col>
      </m-row>

      <m-row v-if="unlocked_field('email2')">

        <m-col lg="12">

          <memlist-text-input
            class="mt-4"
            v-model="translated_email_status"
            :title="$t('MEMBER.EMAIL2_STATUS')"
            layout="horizontal"
            size="sm"
            :disabled="true"
          />
        </m-col>
      </m-row>


      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('phone')"
        :title="$t('MEMBER.PHONE')"
        layout="horizontal"
        size="sm"
        v-model="local_member.phone"
        :type="secret_type"
        @input="change_data"
        :required="constreq.includes('MEMBER_REQ_PHONE')"
        :state="validate_field(v$.local_member.phone)"
      />

      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('landline')"
        v-model="local_member.landline"
        :title="$t('MEMBER.LANDLINE')"
        layout="horizontal"
        :type="secret_type"
        size="sm"
        @input="change_data"
      />

      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('public_id')"
        v-model="local_member.public_id"
        :title="$t('MEMBER.PUBLIC_ID')"
        layout="horizontal"
        size="sm"
        @input="change_public_id"
      />

      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('avd')"
        v-model="local_member.avd"
        :title="$t('MEMBER.AVD')"
        layout="horizontal"
        size="sm"
        @input="change_data"
      />

      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('cname')"
        v-model="local_member.cname"
        :title="$t('MEMBER.CNAME')"
        layout="horizontal"
        size="sm"
        @input="change_data"
        :state="validate_field(v$.local_member.cname)"
      />

      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('org_number')"
        v-model="local_member.org_number"
        :title="$t('MEMBER.ORG_NUMBER')"
        layout="horizontal"
        size="sm"
        @input="change_data"
        :state="validate_field(v$.local_member.org_number)"
        :required="constreq.includes('MEMBER_REQ_ORG_NUMBER')"
      />

      <memlist-text-input
        class="mt-4"
        v-if="unlocked_field('student_id')"
        v-model="local_member.student_id"
        :title="$t('MEMBER.STUDENT_ID')"
        layout="horizontal"
        size="sm"
        @input="change_data"
        :disabled="true"
      />

      <memlist-select-input2
        class="mt-4"
        v-if="unlocked_field('public_sex')"
        v-model="local_member.public_sex"
        :items="gender_options"
        :title="$t('MEMBER.PUBLIC_SEX')"
        layout="horizontal"
        size="sm"
        @input="change_data"
        validation_type="TEXT"
      />

      <memlist-select-input2
        class="mt-4"
        v-if="unlocked_field('sex')"
        v-model="local_member.sex"
        :items="gender_options"
        :title="$t('MEMBER.LEGAL_SEX')"
        layout="horizontal"
        size="sm"
        @input="change_data"
        :disabled="true"
        validation_type="TEXT"
      />


      <m-row class="mt-2">
        <m-col lg="6"></m-col>
        <m-col lg="6" style="padding-left: 0px;">
          <memlist-button variant="outline-success" size="sm" @click="open_password_modal">{{$t('ACCOUNT.UPDATE_PASSWORD')}}</memlist-button>
        </m-col>
      </m-row>


      <memlist-select-input2
        class="mt-4"
        v-if="unlocked_field('lang')"
        v-model="local_member.lang"
        :items="lang_options"
        :title="$t('MEMBER.LANG')"
        layout="horizontal"
        size="sm"
        @input="change_data"
        validation_type="TEXT"
      />

      <RightSaveButton
        ref="save-button"
        :text="
          !this.creating
            ? $t('COMMON.SAVE')
            : $t('COMMON.CREATE')
        "
        @clicked="submit_clicked()"
      />

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';
import { validatePersonnr } from '@/core/services/personnr';
import { validate_email } from '@/core/services/email';
import { useVuelidate } from '@vuelidate/core'
import { minLength, required, email, requiredIf } from '@vuelidate/validators';
import { helpers } from '@vuelidate/validators'
import axios from 'axios';
import dayjs from 'dayjs';
import RightSaveButton from '@/view/components/buttons/RightSaveButton.vue';
import SPARLookup from '@/view/components/SPARLookup.vue';
import MemberPasswordUpdate from '@/view/components/member_editor_v2/MemberPasswordUpdate.vue';
import ErrorPopup from '@/view/components/ErrorPopup.vue';

const CONSTANT_REQUIREMENTS = [
  'MEMBER_REQ_PERSONNR',
  'MEMBER_REQ_SSN',
  'MEMBER_REQ_AGE',
  'MEMBER_REQ_SEX',
  'MEMBER_REQ_ADDRESS',
  'MEMBER_REQ_FULLNAME',
  'MEMBER_REQ_EMAIL',
  'MEMBER_REQ_PHONE',
  'MEMBER_REQ_EMAILPHONE',
  'MEMBER_REQ_AVD',
  'MEMBER_REQ_ORG_NUMBER',
  'MEMBER_REQ_CNAME',
];


const date_format = 'YYYY-MM-DD'
const must_validate_personnr = (value) => validatePersonnr(value)
const must_validate_dob = (value) => dayjs(value, { date_format }).format(date_format) === value;

export default {

  name: 'PersonalDetailsForm',

  props: ['member','requirements','unlocks'],

  mixins: [ toasts ],

  emits: ['update','validated','change','create','select_member'],

  // --- begin vuelidate --- //
  setup () {
    return { v$: useVuelidate() }
  },

  validations () {
    const is_dob_required = this.constreq?.includes('MEMBER_REQ_PERSONNR');
    const is_in_personnr_required = this.constreq?.includes('MEMBER_REQ_SSN');
    const is_email_required = this.constreq?.includes('MEMBER_REQ_EMAIL');
    const is_name_required = this.constreq?.includes('MEMBER_REQ_FULLNAME');
    const is_cname_required = this.constreq?.includes('MEMBER_REQ_CNAME');
    const is_org_number_required = this.constreq?.includes('MEMBER_REQ_ORG_NUMBER');
    const is_phone_required = this.constreq?.includes('MEMBER_REQ_PHONE');
    

    return {
      local_member: {

        phone: {
          required: requiredIf(() => is_phone_required),

          // validation fn
          validation(val) {
            return minLength(1);
          },

          $autoDirty: false
        },


        // firstname field
        firstname: {
          required: requiredIf(() => is_name_required),

          // validation fn
          validation(val) {
            return minLength(1);
          },

          $autoDirty: false
        },

        // lastname field
        lastname: {
          required: requiredIf(() => is_name_required),

          // validation fn
          validation(val) {
            return minLength(1);
          },

          $autoDirty: false

        },

        cname: {
          required: requiredIf(() => is_cname_required),

          // validation fn
          validation(val) {
            return minLength(1);
          },

          $autoDirty: false
        },
        
        org_number: {
          required: requiredIf(() => is_org_number_required),

          // validation fn
          validation(val) {
            return minLength(10);
          },

          $autoDirty: false
        },

        // birhday field
        dob: {

          // required condition
          required: requiredIf(() => is_dob_required),

          // validation fn
          validation(val) {
            if (is_dob_required) return must_validate_dob(val);

            return true;
          },

          $autoDirty: false
        },

        // person number field
        in_personnr: {

          // required condition
          required: requiredIf(() => is_in_personnr_required),

          // validation fn
          validation(val) {
            if (is_in_personnr_required) return must_validate_personnr(val).valid;

            return true;
          },

          $autoDirty: false
        },

        // email field
        email: {

          // required condition
          required: requiredIf(() => is_email_required),

          // validation fn
          email,

          $autoDirty: false
        },

        email2: {

          // required condition
          required: false,

          // validation fn
          email,

          $autoDirty: false
        },
      }
    }
  },

  // -- end vuelidate -- //

  components: {
    MemberPasswordUpdate,
    RightSaveButton,
    SPARLookup,
    ErrorPopup
  },

  watch: {
    member: {
      deep: true, // check properties of the form, not just the form reference (example.name for example)
      handler(val) {
        this.local_member = { ...this.member };
        this.show_error_spar = false;

        this.duplicate_personnr = [];
        this.duplicate_emails = [];

        if (this.local_member.in_personnr) {
          this.fetch_duplicates('personnr', this.local_member.in_personnr);
        }

        if (this.local_member.email) {
          this.fetch_duplicates('email', this.local_member.email);
        }
      }
    }
  },

  computed: {
    translated_email_status() {
      return this.local_member.email_status ? this.$t('MEMBER.EMAIL_STATUSES.' + this.local_member.email_status.toUpperCase()) + ` (${this.local_member.email_status_at ? dayjs(this.local_member.email_status_at).format('YYYY-MM-DD') : '-'})`: '';
    },
    ...mapGetters(['currentCompanyId', 'currentPeriodId', 'currentUserId', 'companies', 'periods', 'profile', 'settings','isTHS']),
    constreq() {
      return this.requirements.map(item => {
        return CONSTANT_REQUIREMENTS[item.req_id];
      });
    },
    secret_type() {
      return this.local_member.secret ? 'password' : 'text';
    },
    is_swessn() {
      return this.valid_personnr ? this.$t('COMMON.YES') : this.$t('COMMON.NO');
    },
    valid_personnr() {
      const result = this.validate_personnr(this.local_member.in_personnr);

      return result.valid;
    },
    creating() {
      return !(this.local_member && this.local_member.member_id);
    }
  },

  methods: {

    async fetch_duplicates(type, value) {
      const payload = {
        personnr: type === 'personnr' ? value : undefined,
        email: type === 'email' ? value : undefined,
        member_id: this.local_member.member_id,
      };

      const res = await axios.post(`/member/duplicates/${type}`, payload);

      if (res.status === 200) {
        if (type === 'personnr') {
          this.duplicate_personnr = res.data;
        }
        else if (type === 'email') {
          this.duplicate_emails = res.data;
        }
      }
    },

    select_member(member_id) {
      this.duplicate_personnr = [];
      this.duplicate_emails = [];
      
      this.$emit('select_member', member_id);
    },
    assign_if_set(key, val) {
      if (val && (val+'').length > 0) {
        this.local_member[key] = val;
      }
    },

    save_from_spar(spar_data, personnr, fmt_personnr, sex) {
      this.assign_if_set('firstname', spar_data.fn);
      this.assign_if_set('lastname', spar_data.ln);
      this.assign_if_set('address', spar_data.st);
      this.assign_if_set('zipcode', spar_data.zip);
      this.assign_if_set('post', spar_data.pa);
      this.assign_if_set('co', spar_data.co);

      if (spar_data.date_of_death) {
        this.local_member.deceased = true;
        this.local_member.deactivate_at = spar_data.date_of_death;
        this.local_member.skv_status = 'PEN_DELETED';
      }

      if (spar_data.is_protected) {
        this.local_member.secret = true;
      }

      if (spar_data.is_deactivated) {
        this.local_member.deactivate_at = spar_data.deactivate_at || dayjs().format('YYYY-MM-DD');
        this.local_member.deactivate_reason = spar_data.deactivate_reason;
        this.local_member.deactivated = true;
        this.local_member.skv_status = 'PEN_DELETED';
      }

      if (spar_data.bd) {
        this.assign_if_set('dob', spar_data.bd.y + '-' + spar_data.bd.m + '-' + spar_data.bd.d );
      }

      this.$emit('save_from_spar', this.local_member);
    },

    validate_personnr(personnr) {
      return validatePersonnr(personnr);
    },

    open_spar_popup() {

      this.$refs['spar-lookup-popup'].show();
    },

    unlocked_field(name) {
      const obj = this.unlocks.find(item => item.name === name);

      return obj !== undefined && obj.value;
    },

    open_password_modal() {
      this.$refs['passwordModal'].show();
    },

    async check_email(email) {
      const email_valid = validate_email(email);

      if (this.local_member.email) {
        this.fetch_duplicates('email', this.local_member.email);
      }
      
      if (email_valid && this.constreq?.includes('MEMBER_REQ_EMAIL')) {

        const res = await axios.post(`/member/search/email`, { email: email, not_member_id: this.local_member.member_id });

        if (res.status === 200) {
          this.error_popup_message = 'ERR_MEMBER_ALREADY_EXISTS';

          this.$nextTick(()=>{ this.error_popup_message = null; });

          return;
        }

        this.$emit('change', this.local_member);
      }

      
    },

    async change_email2() {
      this.check_email(this.local_member.email2);
    },

    async change_email() {
      this.check_email(this.local_member.email);
    },

    async change_public_id() {

      if (this.local_member.public_id) {

        const res = await axios.post(`/member/search/public_id`, { public_id: this.local_member.public_id });

        if (res.status === 200) {
          this.error_popup_message = 'ERR_MEMBER_ALREADY_EXISTS';

          this.$nextTick(()=>{ this.error_popup_message = null; });

          return;
        }

        this.$emit('change', this.local_member);
      }

    },

    async change_personnr() {
      this.local_member.swessn = this.valid_personnr;

      if (this.creating && this.valid_personnr && this.constreq?.includes('MEMBER_REQ_SSN')) {

        const res = await axios.post(`/member/search/personnr`, { personnr: this.local_member.in_personnr });

        if (res.status === 200) {
          this.error_popup_message = 'ERR_MEMBER_ALREADY_EXISTS';

          this.$nextTick(()=>{ this.error_popup_message = null; });

          return;
        }

        const result = this.validate_personnr(this.local_member.in_personnr);

        if (result.valid) {
          this.local_member.dob = result.birthdate;
          this.local_member.in_personnr = result.formatted;
        }

        this.$emit('change', this.local_member);
      }
      else {
        const result = this.validate_personnr(this.local_member.in_personnr);

        if (result.valid) {
          this.local_member.dob = result.birthdate;
          this.local_member.in_personnr = result.formatted;
        }

        this.$emit('change', this.local_member);

        this.fetch_duplicates('personnr', this.local_member.in_personnr);
      }

    },

    /// when the form has changed
    change_data() {
      this.$emit('change', this.local_member);
    },

    async submit_clicked() {
      this.v$.$touch();

      if (this.local_member.hd === 1) {
        const valid = await this.v$.$validate();

        if (!valid) {
          // show error toast if it's not validated
          this.toastr(
            'danger',
            this.$t('COMMON.ERROR'),
            this.$t('COMMON.FORM_VALIDATION_FAILED')
          );

          this.$refs['save-button'].stop();

          return;
        }
      }
      

      if (this.creating) {
        this.$emit('create', this.local_member);
      }
      else {
        this.$emit('update', this.local_member);
      }

      this.$refs['save-button'].stop();
    },

    validate_field(field) {

      // not touched
      if (!field.$dirty) {
        return null;
      }

      // touched and invalid
      if (field.$invalid) return false;

      // touched and valid
      return true;
    }
  },

  mounted() {
    this.show_form = this.expanded;
    this.show_error_spar = false;

    if (this.member) {
      this.local_member = { ...this.member };
    }
  },

  data() {
    return {
      duplicate_personnr: [ ],
      duplicate_emails: [ ],
      error_popup_message: null,

      show_error_spar: false,

      local_member: {},

      show_form: true,

      gender_options: [
        { text: this.$t('COMMON.MAN'), value: 'M' },
        { text: this.$t('COMMON.WOMAN'), value: 'F' },
        { text: this.$t('COMMON.OTHER'), value: 'O' },
        { text: this.$t('COMMON.UNKNOWN'), value: 'U' },
      ],

      lang_options: [
        { text: this.$t('COMMON.SV'), value: 'sv' },
        { text: this.$t('COMMON.EN'), value: 'en' },
      ],

    };
  }
};

</script>

<style lang="scss" scoped>


.error-spar {
  font-style: italic;
  color: rgb(144, 47, 47);
}

/* give the input some room so the icon doesn't overlap text */
:deep(.personnr-row input) {
  padding-right: 2.2rem;
}

/* position the icon inside the input, 10 px from the right edge */
.personnr-status-icon {
  position: absolute;
  right: 18px;
  top: 60%;
  transform: translateY(-50%);
  pointer-events: none;      /* keeps input clickable */
}


</style>
